import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Tooltip,
    FormHelperText,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';
import {
    UploadFileOutlined,
    VisibilityOutlined,
    DeleteOutline,
} from '@mui/icons-material';
import { useField, useFormikContext } from 'formik';
import { REACT_APP_CDN_URL } from '../../../../../../../../utils/base-urls';

interface MultiFileUploadFieldProps {
    name: string;
    label: string;
    accept?: string;
}

const getFileUrl = (path: string) => {
    if (/^https?:\/\//i.test(path)) return path;
    return `${REACT_APP_CDN_URL}${path}`;
};

const isPdf = (file: File | string) => {
    if (file instanceof File) {
        return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    }
    return file.toLowerCase().endsWith('.pdf');
};

const displayName = (file: File | string) => {
    if (file instanceof File) return file.name;
    return file.split('/').pop() || 'Uploaded file';
};

const MultiFileUploadField: React.FC<MultiFileUploadFieldProps> = ({
    name,
    label,
    accept = 'application/pdf',
}) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const [, meta] = useField(name);
    const files: Array<File | string> = Array.isArray(values[name]) ? values[name] : [];

    const showError = meta.touched && Boolean(meta.error);

    const [openDialog, setOpenDialog] = useState(false);
    const [previewURL, setPreviewURL] = useState<string | null>(null);
    const [previewIsPdf, setPreviewIsPdf] = useState(true);

    const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.currentTarget.files;
        if (!selected || selected.length === 0) return;
        const next = [...files, ...Array.from(selected)];
        setFieldValue(name, next);
        // Reset input so selecting the same file again still fires onChange
        e.currentTarget.value = '';
    };

    const handleRemove = (idx: number) => {
        const next = files.filter((_, i) => i !== idx);
        setFieldValue(name, next);
    };

    const handleView = (file: File | string) => {
        if (file instanceof File) {
            const url = URL.createObjectURL(file);
            setPreviewURL(url);
            setPreviewIsPdf(isPdf(file));
            setOpenDialog(true);
            return;
        }
        const url = getFileUrl(file);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setPreviewURL(null);
    };

    return (
        <Box display="flex" flexDirection="column" mt={1}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                    endIcon={<UploadFileOutlined />}
                    variant="contained"
                    component="label"
                    size="small"
                    color={showError ? 'error' : 'primary'}
                >
                    {label}
                    <input
                        type="file"
                        hidden
                        multiple
                        accept={accept}
                        onChange={handleAddFiles}
                    />
                </Button>
            </Box>

            {files.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {files.map((file, idx) => (
                        <Chip
                            key={idx}
                            label={displayName(file)}
                            variant="outlined"
                            sx={{ maxWidth: 220 }}
                            onClick={() => handleView(file)}
                            onDelete={() => handleRemove(idx)}
                            deleteIcon={
                                <Tooltip title="Remove">
                                    <DeleteOutline />
                                </Tooltip>
                            }
                            icon={
                                <Tooltip title="View">
                                    <VisibilityOutlined fontSize="small" />
                                </Tooltip>
                            }
                        />
                    ))}
                </Stack>
            )}

            {showError && (
                <FormHelperText error sx={{ ml: 1 }}>
                    {typeof meta.error === 'string' ? meta.error : 'Invalid file(s)'}
                </FormHelperText>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>View Uploaded File</DialogTitle>
                <DialogContent>
                    {previewURL && previewIsPdf ? (
                        <iframe
                            src={previewURL}
                            width="100%"
                            height="500px"
                            title="PDF Viewer"
                            style={{ border: 'none' }}
                        />
                    ) : previewURL ? (
                        <img src={previewURL} alt="uploaded-file" width="100%" />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MultiFileUploadField;
